/**
 * Drag and Drop Utility Functions
 * Common functions for task and status drag operations
 */

/**
 * Creates a drag image with preserved layout and styling
 * @param {HTMLElement} element - The element to clone for drag image
 * @param {number} offsetX - X offset for drag image positioning
 * @param {number} offsetY - Y offset for drag image positioning
 * @returns {HTMLElement} - The created drag element
 */
export const createDragImage = (element, offsetX, offsetY) => {
  const dragElement = element.cloneNode(true);
  const computedStyle = window.getComputedStyle(element);
  
  // Apply drag styling and preserve layout
  dragElement.className = 'drag-image-shadow';
  dragElement.style.width = element.offsetWidth + 'px';
  dragElement.style.height = element.offsetHeight + 'px';
  dragElement.style.display = computedStyle.display;
  dragElement.style.flexDirection = computedStyle.flexDirection;
  dragElement.style.alignItems = computedStyle.alignItems;
  dragElement.style.justifyContent = computedStyle.justifyContent;
  dragElement.style.padding = computedStyle.padding;
  
  document.body.appendChild(dragElement);
  return dragElement;
};

/**
 * Creates auto-scroll handler for drag operations
 * @param {number} scrollZone - Distance from edge to trigger scroll
 * @param {number} scrollSpeed - Speed of scrolling
 * @param {boolean} isHorizontal - Whether to scroll horizontally
 * @returns {Function} - Event handler function
 */
export const createAutoScroll = (scrollZone, scrollSpeed, isHorizontal = false) => (e) => {
  e.preventDefault();
  const rect = e.currentTarget.getBoundingClientRect();
  const pos = isHorizontal ? e.clientX - rect.left : e.clientY - rect.top;
  const size = isHorizontal ? rect.width : rect.height;
  const scrollProp = isHorizontal ? 'scrollLeft' : 'scrollTop';
  
  if (pos < scrollZone && e.currentTarget[scrollProp] > 0) {
    e.currentTarget[scrollProp] -= scrollSpeed;
  } else if (pos > size - scrollZone) {
    e.currentTarget[scrollProp] += scrollSpeed;
  }
};

/**
 * Calculates drop position for drag operations
 * @param {Event} e - Drag event
 * @param {boolean} isVertical - Whether calculation is for vertical layout
 * @returns {string} - 'left' or 'right' for horizontal, 'top' or 'bottom' for vertical
 */
export const calculateDropPosition = (e, isVertical = false) => {
  const rect = e.currentTarget.getBoundingClientRect();
  const mouse = isVertical ? e.clientY - rect.top : e.clientX - rect.left;
  const size = isVertical ? rect.height : rect.width;
  const zone1 = size * 0.4;
  const zone2 = size * 0.6;
  
  return mouse < zone1 ? 'left' : mouse > zone2 ? 'right' : mouse < (size / 2) ? 'left' : 'right';
};

/**
 * Calculates drop position for list view status reordering
 * @param {Event} e - Drag event
 * @param {string} statusId - Current status ID
 * @param {Array} statusEntries - Array of [statusId, statusData] entries
 * @param {Array} statuses - Array of status objects for index calculation
 * @param {Object} draggedStatus - Currently dragged status object
 * @returns {Object} - Drop position info with position, targetIndex, and isGhost flag
 */
export const calculateListDropPosition = (e, statusId, statusEntries, statuses, draggedStatus) => {
  if (!e?.currentTarget || !statusEntries || !statuses || !draggedStatus) return null;
  
  const currentIndex = statusEntries.findIndex(([id]) => id === statusId);
  if (currentIndex === -1) return null;
  
  const rect = e.currentTarget.getBoundingClientRect();
  const mouseY = e.clientY - rect.top;
  const height = rect.height;
  
  let position, targetIndex;
  
  // Determine drop zone based on mouse position and item boundaries
  if (currentIndex === 0 && (mouseY < height * 0.5 || mouseY < -height * 0.5)) {
    // Top boundary: dropping above first item (extends 50% outside accordion)
    position = 'top';
    targetIndex = 0;
  }
  else if (currentIndex === statusEntries.length - 1 && mouseY > height * 0.5) {
    // Bottom boundary: dropping below last item
    position = 'bottom';
    targetIndex = statusEntries.length;
  }
  else if (mouseY > height * 0.5) {
    // Lower half: insert after current item
    position = 'between';
    targetIndex = currentIndex + 1;
  } else {
    // Upper half: insert before current item
    position = 'between';
    targetIndex = currentIndex;
  }
  
  // Ghost detection: check if drop would result in same position
  const draggedIndex = statuses.findIndex(s => s.id === draggedStatus?.id);
  if (draggedIndex === -1 || targetIndex === undefined) return null;
  
  // Calculate final insertion index after removal (decrement because dragged item is removed first)
  let finalIndex = targetIndex;
  if (draggedIndex < targetIndex) {
    finalIndex = targetIndex - 1;
  }
  
  // Clamp to valid bounds
  finalIndex = Math.max(0, Math.min(finalIndex, statuses.length - 1));
  
  // Check for ghost drops (no actual movement)
  const isGhost = finalIndex === draggedIndex;
  
  return { position, targetIndex, isGhost };
};

/**
 * Helper to toggle CSS classes on elements
 * @param {string|HTMLElement} selector - CSS selector or element
 * @param {Array} remove - Classes to remove
 * @param {string} add - Class to add
 */
const toggleClasses = (selector, remove = [], add = null) => {
  const elements = typeof selector === 'string' ? document.querySelectorAll(selector) : [selector];
  elements.forEach(el => {
    if (el) {
      el.classList.remove(...remove);
      if (add) el.classList.add(add);
    }
  });
};

/**
 * Applies visual feedback to header elements during drag
 * @param {HTMLElement} container - Container element
 * @param {string} position - Position class to apply
 */
export const applyHeaderVisualFeedback = (container, position) => {
  const headerElement = container.querySelector('.status-header-draggable');
  if (headerElement) {
    toggleClasses(headerElement, ['drag-left', 'drag-right', 'drag-left-ghost', 'drag-right-ghost'], `drag-${position}`);
  }
};

/**
 * Clears visual feedback from header elements
 * @param {HTMLElement} container - Container element
 */
export const clearHeaderVisualFeedback = (container) => {
  const headerElement = container.querySelector('.status-header-draggable');
  if (headerElement) {
    toggleClasses(headerElement, ['drag-left', 'drag-right', 'drag-left-ghost', 'drag-right-ghost']);
  }
};

/**
 * Creates a visual manager for drag operations
 * @param {Object} config - Configuration with classNames and dataAttribute
 * @returns {Object} - Manager with apply and clear methods
 */
export const createDragVisualManager = (config) => {
  const { classNames, dataAttribute } = config;
  return {
    apply: (targetId, className) => {
      toggleClasses(`.${classNames.join(', .')}`, classNames);
      const targetElement = document.querySelector(`[${dataAttribute}="${targetId}"]`);
      if (targetElement && className) targetElement.classList.add(className);
    },
    clear: () => toggleClasses(`.${classNames.join(', .')}`, classNames)
  };
};

/**
 * Creates generic task drag handlers
 * @param {Object} config - Configuration with callbacks and refs
 * @returns {Object} - Drag event handlers
 */
export const createTaskDragHandlers = (config) => {
  const { onDragStart, onDragEnd, visualManager, dragOverRef } = config;
  return {
    handleDragStart: (e, task) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', task.id);
      const dragElement = createDragImage(e.currentTarget, e.nativeEvent.offsetX, e.nativeEvent.offsetY);
      e.dataTransfer.setDragImage(dragElement, e.nativeEvent.offsetX, e.nativeEvent.offsetY);
      requestAnimationFrame(() => {
        if (dragElement && dragElement.parentNode === document.body) {
          document.body.removeChild(dragElement);
        }
      });
      onDragStart(task);
    },
    handleDragEnd: () => {
      dragOverRef.current = null;
      visualManager.clear();
      onDragEnd();
    },
    handleDragOver: (e, targetId, draggedItem) => {
      if (!draggedItem) return;
      e.preventDefault();
      if (dragOverRef.current !== targetId) {
        dragOverRef.current = targetId;
        visualManager.apply(targetId, 'drag-over-column');
      }
    },
    handleDragLeave: (e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) {
        dragOverRef.current = null;
        visualManager.clear();
      }
    },
    handleDrop: (e, targetId, onDrop) => {
      e.preventDefault();
      dragOverRef.current = null;
      visualManager.clear();
      onDrop(targetId);
    }
  };
};

/**
 * Generic cleanup function for drag visual feedback
 * Removes all drag-related classes and indicators
 */
export const cleanupDragVisuals = (dragOverRef = null) => {
  // Clear drag over reference
  if (dragOverRef?.current) {
    dragOverRef.current.position = undefined;
    dragOverRef.current.targetIndex = undefined;
  }
  
  // Remove all drag-related classes
  document.querySelectorAll('.accordion-summary-status-drag-over, .dragged-status, .ghost-dragged-status, .list-drag-over-status, .accordion-summary-ticket-drag-over').forEach(el => {
    el.classList.remove('accordion-summary-status-drag-over', 'dragged-status', 'ghost-dragged-status', 'list-drag-over-status', 'accordion-summary-ticket-drag-over');
  });
  
  // Remove all drop indicators
  document.querySelectorAll('.status-drop-indicator, .status-drop-indicator-ghost').forEach(el => {
    el.remove();
  });
};

/**
 * Creates accordion-specific drag visual feedback manager
 * @param {Set} expandedStatuses - Set of expanded status IDs
 * @returns {Object} - Manager with feedback methods
 */
export const createAccordionDragManager = (expandedStatuses) => ({
  applyTicketDragFeedback: (statusId) => {
    toggleClasses('.list-drag-over-status, .accordion-summary-ticket-drag-over', ['list-drag-over-status', 'accordion-summary-ticket-drag-over']);
    const targetElement = document.querySelector(`[data-status-id="${statusId}"]`);
    if (targetElement) targetElement.classList.add('list-drag-over-status');
    const accordionSummary = document.querySelector(`[data-accordion-summary="${statusId}"]`);
    if (accordionSummary && !expandedStatuses.has(statusId)) {
      accordionSummary.classList.add('accordion-summary-ticket-drag-over');
    }
  },
  clearTicketDragFeedback: () => cleanupDragVisuals()
});

/**
 * Creates drop position indicator element
 * @param {Object} dropInfo - Drop position information
 * @param {HTMLElement} container - Container element
 * @param {Array} statusEntries - Status entries array
 */
const createDropIndicator = (dropInfo, container, statusEntries) => {
  const indicator = document.createElement('div');
  indicator.className = dropInfo.isGhost ? 'status-drop-indicator-ghost' : 'status-drop-indicator';
  indicator.style.cssText = 'height: 2px; width: 90%; margin: 8px 0 8px auto; position: relative;';
  
  if (dropInfo.position === 'top') {
    container.insertBefore(indicator, container.firstChild);
  } else if (dropInfo.position === 'bottom') {
    container.appendChild(indicator);
  } else if (dropInfo.position === 'between') {
    const accordions = container.querySelectorAll('.MuiAccordion-root');
    const targetAccordion = accordions[dropInfo.targetIndex];
    if (targetAccordion && container.contains(targetAccordion)) {
      container.insertBefore(indicator, targetAccordion);
    } else if (dropInfo.targetIndex === statusEntries.length) {
      container.appendChild(indicator);
    }
  }
};
/**
 * Creates status drag handlers for list view with position calculation
 * @param {Object} config - Configuration object with all required props
 * @returns {Object} - Drag handler functions
 */
export const createStatusDragHandlers = (config) => {
  const {
    draggedStatus,
    canReorderStatus,
    statusEntries,
    statuses,
    onDragOver,
    onDragLeave,
    onDrop,
    dragOverStatusRef,
    dispatch,
    selectedProject,
    t,
    reorderStatuses,
    updateStatusOrder,
    toast,
    performStatusReorder
  } = config;

  return {
    dragOver: (e, statusId) => {
      // Check permissions and drag state
      if (!draggedStatus || !canReorderStatus) {
        onDragOver?.(e, statusId); // Fallback to task drag
        return;
      }
      
      if (!e?.currentTarget) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      
      // Calculate where item would be dropped and detect ghost drops
      const dropInfo = calculateListDropPosition(e, statusId, statusEntries, statuses, draggedStatus);
      if (dropInfo) {
        dragOverStatusRef.current = { statusId, ...dropInfo };
        
        // Clear previous visual feedback
        cleanupDragVisuals();
        
        // Apply hover visual feedback only if we have a valid drop position
        if (!dropInfo.isGhost) {
          const targetElement = document.querySelector(`[data-accordion-summary="${statusId}"]`);
          if (targetElement) {
            targetElement.classList.add('accordion-summary-status-drag-over');
          }
        }
        
        // Apply ghost visual to dragged element
        const draggedElement = document.querySelector(`[data-accordion-summary="${draggedStatus.id}"]`);
        if (draggedElement) {
          draggedElement.classList.add('ghost-dragged-status');
        }
        
        // Create drop position indicators
        const container = e.currentTarget.closest('.MuiBox-root');
        if (container && dropInfo) {
          createDropIndicator(dropInfo, container, statusEntries);
        }
      } else {
        // No drop zone - clear any existing indicators
        dragOverStatusRef.current = null;
        cleanupDragVisuals();
      }
    },
    
    dragLeave: (e) => {
      if (!draggedStatus || !canReorderStatus) {
        onDragLeave?.(e);
        return;
      }
      
      if (!e?.currentTarget?.contains(e.relatedTarget)) {
        cleanupDragVisuals(dragOverStatusRef);
      }
    },
    
    drop: (e, statusId) => {
      if (!draggedStatus || !canReorderStatus) {
        onDrop?.(e, statusId);
        return;
      }
      
      if (!e?.currentTarget) return;
      e.preventDefault();
      
      // Recalculate drop position at drop time to ensure consistency
      const dropInfo = calculateListDropPosition(e, statusId, statusEntries, statuses, draggedStatus);
      dragOverStatusRef.current = null;
      
      // Clear all visual feedback and indicators
      cleanupDragVisuals(dragOverStatusRef);
      
      if (!draggedStatus || !dropInfo) {
        return;
      }
      
      // Skip if ghost drop (would result in same position)
      if (dropInfo.isGhost) {
        return;
      }
      
      // Use performStatusReorder if available, otherwise fallback to direct dispatch
      const draggedIndex = statuses?.findIndex(s => s.id === draggedStatus.id) ?? -1;
      if (draggedIndex !== -1 && dropInfo.targetIndex !== undefined) {
        if (performStatusReorder) {
          // Use the centralized reorder function with proper target index
          performStatusReorder(draggedIndex, dropInfo.targetIndex, 'between');
        } else if (dispatch && statuses) {
          // Fallback to direct dispatch (legacy) - decrement because dragged item is removed first
          const reorderedStatuses = [...statuses];
          const [removed] = reorderedStatuses.splice(draggedIndex, 1);
          
          let insertIndex = dropInfo.targetIndex;
          if (draggedIndex < insertIndex) {
            insertIndex--;
          }
          
          // Clamp to valid bounds
          insertIndex = Math.max(0, Math.min(insertIndex, reorderedStatuses.length));
          reorderedStatuses.splice(insertIndex, 0, removed);

          dispatch(reorderStatuses({ statuses: reorderedStatuses, previousOrder: statuses }));
          const statusOrder = reorderedStatuses.map(s => s.id);
          dispatch(updateStatusOrder({ projectId: selectedProject, statusOrder }))
            .unwrap()
            .then(() => toast?.success(t('dashboard.statusOrderUpdatedSuccess')))
            .catch((error) => {
              toast?.error(t('dashboard.statusOrderUpdateError'));
            });
        }
      }
    }
  };
};

/**
 * Creates board column drag handlers with position calculation
 * @param {Object} config - Configuration object
 * @returns {Object} - Drag handler functions
 */
export const createBoardColumnDragHandlers = (config) => {
  const { draggedStatus, canReorderStatus, statuses, performStatusReorder, dispatch, setDraggedStatus, dragPositionRef } = config;

  return {
    dragOver: (e, statusId) => {
      if (!draggedStatus || !canReorderStatus || !e?.currentTarget || !statuses) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      
      const position = calculateDropPosition(e, false);
      const draggedIndex = statuses.findIndex(s => s.id === draggedStatus.id);
      const targetIndex = statuses.findIndex(s => s.id === statusId);
      
      if (draggedIndex === -1 || targetIndex === -1) return;
      
      // Calculate final position after insertion (position: 'left'/'right')
      let finalIndex = targetIndex;
      if (position === 'right') {
        finalIndex = targetIndex + 1;
      }
      // Decrement because dragged item is removed first
      if (draggedIndex < finalIndex) {
        finalIndex--;
      }
      
      // Clamp to valid bounds
      finalIndex = Math.max(0, Math.min(finalIndex, statuses.length - 1));
      
      // Check for ghost drops (would result in same position)
      const isGhostDrop = finalIndex === draggedIndex;
      
      dragPositionRef.current = { statusId, position, targetIndex, isGhost: isGhostDrop };
      applyHeaderVisualFeedback(e.currentTarget, isGhostDrop ? `${position}-ghost` : position);
    },

    dragLeave: (e, statusId) => {
      if (!draggedStatus || !canReorderStatus || !e?.currentTarget) return;
      if (!e.currentTarget.contains(e.relatedTarget)) {
        dragPositionRef.current = null;
        clearHeaderVisualFeedback(e.currentTarget);
      }
    },

    drop: (e, targetStatusId) => {
      if (!draggedStatus || !canReorderStatus || !e?.currentTarget) return;
      e.preventDefault();
      
      const dropInfo = dragPositionRef.current;
      dragPositionRef.current = null;
      clearHeaderVisualFeedback(e.currentTarget);
      
      if (!draggedStatus || draggedStatus.id === targetStatusId || dropInfo?.isGhost) {
        dispatch?.(setDraggedStatus(null));
        return;
      }

      const draggedIndex = statuses?.findIndex(s => s.id === draggedStatus.id) ?? -1;
      const targetIndex = statuses?.findIndex(s => s.id === targetStatusId) ?? -1;
      
      if (draggedIndex !== -1 && targetIndex !== -1 && performStatusReorder) {
        performStatusReorder(draggedIndex, targetIndex, dropInfo?.position || 'left');
      }
      
      dispatch?.(setDraggedStatus(null));
    }
  };
};